import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

/** One event's worth of money waiting on the platform balance. */
export interface HeldPayoutSummary {
  eventId: string;
  eventName: string;
  eventStartDate: string | null;
  /** When the event is over. Payouts are gated on this; absent on older backends. */
  eventEndDate?: string | null;
  organizerId: string;
  organizerEmail: string;
  organizerName: string;
  connectedAccountId: string | null;
  currency: string;
  paymentsCount: number;
  /** Total charged to buyers, in minor units. */
  grossAmount: number;
  /**
   * What the organizer receives in the CHARGED currency — gross minus the
   * platform fee. This is what the buyer's receipt implies, not necessarily
   * what leaves the balance.
   */
  payoutAmount: number;
  /**
   * The same share restated in the currency the charges settled into — the
   * amount the transfer actually carries. Equals `payoutAmount` for events sold
   * in the platform's own currency. Null on older backends, or before any
   * payment in the group has a recorded settlement.
   */
  settledPayoutAmount: number | null;
  /** Currency of `settledPayoutAmount`. Absent on older backends. */
  settlementCurrency?: string;
  oldestPaymentAt: string;
}

export interface ReleaseArgs {
  eventId: string;
  /**
   * Pay out an event that has not finished yet. The backend refuses by default:
   * the hold is what covers a cancellation, and once the money is on the
   * organizer's account a refund depends on a reversal that can fail.
   */
  force?: boolean;
}

export interface ReleaseResult {
  released: number;
  failed: { paymentId: string; reason: string }[];
}

export const payoutsApi = createApi({
  reducerPath: 'payoutsApi',
  baseQuery,
  tagTypes: ['Payouts'],
  endpoints: (builder) => ({
    getHeldPayouts: builder.query<HeldPayoutSummary[], void>({
      query: () => ({ url: '/admin/payouts/summary' }),
      providesTags: ['Payouts'],
    }),
    releaseEventPayouts: builder.mutation<ReleaseResult, ReleaseArgs>({
      query: ({ eventId, force }) => ({
        url: `/admin/payouts/events/${eventId}/release`,
        method: 'POST',
        body: { force: force === true },
      }),
      invalidatesTags: ['Payouts'],
    }),
  }),
});

export const { useGetHeldPayoutsQuery, useReleaseEventPayoutsMutation } =
  payoutsApi;
